import { useEffect, useState } from "react";
import { GlobalLoaderProvider } from "./context/GlobalLoaderContext";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CMSProvider } from "./context/CMSContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminServers from "./pages/AdminServers";
import AdminAreas from "./pages/AdminAreas";
import AdminNAPBoxes from "./pages/AdminNAPBoxes";
import AdminPlans from "./pages/AdminPlans";
import AdminClients from "./pages/AdminClients";
import AdminNewApplications from "./pages/AdminNewApplications";
import AdminPayments from "./pages/AdminPayments";
import AdminTickets from "./pages/AdminTickets";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import AdminUsers from "./pages/AdminUsers";
import AdminCMS from "./pages/AdminCMS";
import AdminMikroTik from "./pages/AdminMikroTik";
import AdminActivityLogs from "./pages/AdminActivityLogs";
import ClientLogin from "./pages/ClientLogin";
import ClientDashboard from "./pages/ClientDashboard";
import ClientSOA from "./pages/ClientSOA";
import ClientSOADetail from "./pages/ClientSOADetail";
import ClientPayments from "./pages/ClientPayments";
import ClientPaymentSuccess from "./pages/ClientPaymentSuccess";
import ClientPaymentCancel from "./pages/ClientPaymentCancel";
import ClientTickets from "./pages/ClientTickets";
import TechTickets from "./pages/TechTickets";
import TechNewApplications from "./pages/TechNewApplications";
import AgentDashboard from "./pages/AgentDashboard";
import AgentApplications from "./pages/AgentApplications";
import AdminSalesReports from "./pages/AdminSalesReports";
import AdminGeneratedSOA from "./pages/AdminGeneratedSOA";
import AdminCashFlow from "./pages/AdminCashFlow";
import AdminNAPBoxesMap from "./pages/AdminNAPBoxesMap";

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const API_FOR_IMG = `${BACKEND_URL}`;

export { API, API_FOR_IMG };

function ProtectedRoute({ children, allowedRoles }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    axios
      .get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUser(res.data);
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem("token");
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  return (
    <GlobalLoaderProvider>
      <CMSProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />

            {/* Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/servers"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminServers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/areas"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminAreas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/nap-boxes"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminNAPBoxes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/nap-box-network-map"
              element={<AdminNAPBoxesMap />}
            />
            <Route
              path="/admin/plans"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminPlans />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/new-applications"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminNewApplications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sales-reports"
              element={
                <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminSalesReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/generated-soa"
              element={
                <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminGeneratedSOA />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminClients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payments"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cashflow"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminCashFlow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tickets"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminTickets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/announcements"
              element={
                <ProtectedRoute
                  allowedRoles={["admin", "super_admin", "staff"]}
                >
                  <AdminAnnouncements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cms"
              element={
                <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminCMS />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/mikrotik"
              element={
                <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminMikroTik />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/activity-logs"
              element={
                <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminActivityLogs />
                </ProtectedRoute>
              }
            />

            {/* Client Routes */}
            <Route path="/client/login" element={<ClientLogin />} />
            <Route
              path="/client/dashboard"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/soa"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientSOA />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/soa/:soaId"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientSOADetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/payments"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/payment-success"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientPaymentSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/payment-cancel"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientPaymentCancel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/tickets"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientTickets />
                </ProtectedRoute>
              }
            />

            {/* Tech Routes */}
            <Route
              path="/tech/tickets"
              element={
                <ProtectedRoute allowedRoles={["tech"]}>
                  <TechTickets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tech/new-applications"
              element={
                <ProtectedRoute allowedRoles={["tech"]}>
                  <TechNewApplications />
                </ProtectedRoute>
              }
            />

            {/* Agent Routes Dashboard */}
            <Route
              path="/agent/dashboard"
              element={
                <ProtectedRoute allowedRoles={["agent"]}>
                  <AgentDashboard />
                </ProtectedRoute>
              }
            />

            {/* Agent Routes Applications */}
            <Route
              path="/agent/applications"
              element={
                <ProtectedRoute allowedRoles={["agent"]}>
                  <AgentApplications />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </CMSProvider>
    </GlobalLoaderProvider>
  );
}

export default App;
